import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faFilePdf, faImage, faLock } from '@fortawesome/pro-solid-svg-icons';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';

export type AttachmentKind = 'Image' | 'Pdf' | 'Email';

export interface AttachmentDto {
    id: string;
    fileName: string;
    mediaType: string;
    kind: AttachmentKind;
    sizeBytes: number;
    /** Gehört die Datei inzwischen zur Kalkulation? Dann überlebt sie das Gespräch. */
    belongsToProject: boolean;
    createdAt: string;

    /** Seitenzahl bei einem PDF. Über der Grenze der Schnittstelle geht die Datei nicht mit. */
    pageCount?: number | null;
    /** Ausschreibung, Infrastruktur, Geraetedokumentation … — laut Sichtung. */
    documentKind?: string | null;
    /** Wesentlich, Ergaenzend oder Beiwerk. */
    relevance?: string | null;
    /** Ist der Auszug fertig? Nur dann arbeitet der Assistent mit dem Inhalt. */
    analyzed?: boolean;
    /** Steht die Unterlage noch in der Auswertungsschlange? */
    pending?: boolean;
    analysisError?: string | null;
}

/**
 * Der Auswertungsstand als Kurzform für den Chip.
 *
 * **Warum das sichtbar sein muss.** Große Dokumente gehen nicht als Datei ins Gespräch, sondern als
 * Auszug — anders geht es nicht, eine Ausschreibung mit über hundert Seiten passt nicht in eine
 * Anfrage. Solange der Auszug fehlt, hat der Assistent das Dokument NICHT gelesen. Ohne Anzeige
 * sieht das aus wie ein gelesenes, und der Vertriebler verlässt sich auf etwas, das es nicht gibt.
 */
const state = (item: AttachmentDto): { text: string; color: 'default' | 'warning' | 'success' } => {
    if (item.analysisError) return { text: 'Auswertung fehlgeschlagen', color: 'warning' };
    if (item.pending) return { text: 'wird gelesen …', color: 'warning' };
    if (item.analyzed) {
        return item.relevance === 'Beiwerk'
            ? { text: 'Beiwerk', color: 'default' }
            : { text: item.documentKind ?? 'ausgewertet', color: 'success' };
    }
    if (item.kind !== 'Pdf') return { text: '', color: 'default' };
    return { text: 'noch nicht ausgewertet', color: 'warning' };
};

const ICONS: Record<AttachmentKind, typeof faImage> = {
    Image: faImage,
    Pdf: faFilePdf,
    Email: faEnvelope,
};

const readable = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * Die Unterlagen eines Gesprächs, oben am Rand.
 *
 * **Warum am oberen Rand und nicht im Verlauf.** Eine Datei ist kein Gesprächsbeitrag, sondern
 * Zustand: sie liegt für die Dauer des Gesprächs dabei und wirkt auf jede Antwort. Als Blase im
 * Verlauf wäre sie nach zehn Zügen aus dem Blick, obwohl sie weiter mitwirkt — und niemand wüsste
 * mehr, was der Assistent eigentlich vor sich hat.
 *
 * **Warum das Schloss.** Nach dem Übernehmen in den Kalkulator gehört die Datei zur Kalkulation und
 * überlebt das Gespräch. Das ist ein Unterschied, den man sehen muss: alles andere hier wird beim
 * Verwerfen gelöscht.
 */
export const AttachmentBar: React.FC<{
    draftId: string;
    /** Von außen angestoßene Aktualisierung, etwa nach einem Ablegen auf dem Panel. */
    reloadToken?: number;
}> = ({ draftId, reloadToken }) => {
    const theme = useTheme();
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [items, setItems] = useState<AttachmentDto[]>([]);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await http.get<AttachmentDto[]>(`/AiDraft/${draftId}/attachments`);
            setItems(r.data);
        } catch {
            /* Eine nicht ladbare Liste darf das Gespräch nicht aufhalten. */
        }
    }, [http, draftId]);

    useEffect(() => { load(); }, [load, reloadToken]);

    /*
     * Solange eine Unterlage ausgewertet wird, nachfragen. Ohne das bliebe „wird gelesen …" bis zum
     * nächsten Zug stehen — und wer eine Ausschreibung hochlädt, wartet genau auf diesen Wechsel,
     * bevor er zu tippen anfängt.
     */
    useEffect(() => {
        if (!items.some((i) => i.pending)) return undefined;

        const timer = window.setInterval(() => { load(); }, 4000);
        return () => window.clearInterval(timer);
    }, [items, load]);

    const remove = async (item: AttachmentDto) => {
        setBusy(item.id);
        setError(null);
        try {
            await http.delete(`/AiDraft/${draftId}/attachment/${item.id}`);
            setItems((cur) => cur.filter((i) => i.id !== item.id));
        } catch {
            setError(`${item.fileName} konnte nicht entfernt werden.`);
        } finally {
            setBusy(null);
        }
    };

    if (items.length === 0 && !error) return null;

    return (
        <Box
            sx={{
                flexShrink: 0, px: 2, py: 1,
                borderBottom: 1, borderColor: 'divider',
                bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
        >
            {error && <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                    Unterlagen
                </Typography>

                {items.map((item) => (
                    <Tooltip
                        key={item.id}
                        title={[
                            item.fileName,
                            item.pageCount ? `${item.pageCount} Seiten` : '',
                            state(item).text,
                            item.analysisError ?? '',
                            item.belongsToProject
                                ? 'gehört zur Kalkulation und bleibt erhalten'
                                : 'wird mit dem Gespräch gelöscht',
                        ].filter(Boolean).join(' — ')}
                    >
                        <Chip
                            size="small"
                            variant="outlined"
                            icon={busy === item.id
                                ? <CircularProgress size={12} sx={{ ml: 1 }} />
                                : <FontAwesomeIcon icon={ICONS[item.kind]} style={{ marginLeft: 8 }} />}
                            label={(
                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                                    <Box component="span" sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.fileName}
                                    </Box>
                                    <Box component="span" sx={{ color: 'text.disabled', fontSize: 10 }}>
                                        {readable(item.sizeBytes)}
                                    </Box>
                                    {item.belongsToProject && (
                                        <FontAwesomeIcon icon={faLock} fontSize={9} />
                                    )}
                                    {state(item).text && (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: 10,
                                                color: state(item).color === 'success'
                                                    ? 'success.main'
                                                    : state(item).color === 'warning'
                                                        ? 'warning.main'
                                                        : 'text.disabled',
                                            }}
                                        >
                                            {state(item).text}
                                        </Box>
                                    )}
                                </Stack>
                            )}
                            onDelete={busy === item.id ? undefined : () => remove(item)}
                            sx={{ maxWidth: 300 }}
                        />
                    </Tooltip>
                ))}
            </Stack>
        </Box>
    );
};

export default AttachmentBar;
